import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import ChatSheet from "@/components/ChatSheet";
import FloatingAssistant from "@/components/FloatingAssistant";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Configurator from "@/pages/Configurator";
import Quotes from "@/pages/Quotes";
import Profile from "@/pages/Profile";
import ValidatedQuotes from "@/pages/ValidatedQuotes";
import Catalog from "@/pages/Catalog";
import PriceList from "@/pages/PriceList";
import Admin from "@/pages/Admin";
import Reviews from "@/pages/Reviews";
import Contact from "@/pages/Contact";
import Manual from "@/pages/Manual";
import ForgotPassword from "@/pages/ForgotPassword";
import { toast } from "sonner";

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

/** Pythagore admins are internal back-office users — they should never land
 * on the partner Configurator / Catalog / PriceList / Quotes pages. If they
 * type the URL directly, redirect them to `/admin` (their default home). */
function PartnerOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading...</div>;
  // Super Admins (is_admin && is_fortinet_reviewer) are first-class users with
  // partner-side access too — they can build BOMs, browse the catalogue, etc.
  const isSuperAdmin = user?.is_admin && user?.is_fortinet_reviewer;
  if (user?.is_admin && !isSuperAdmin) return <Navigate to="/admin" replace />;
  return children;
}

/** Stricter variant: blocks Pythagore admins AND Fortinet reviewers.
 * Fortinet Reviewers are commercial relays — their workspace is strictly
 * `/reviews`, where they get the inbound queue and an inline BOM editor.
 * All partner-side pages (Configurateur, Catalogue, Liste de prix, Mes
 * devis, Devis validés, Contact) are blocked.
 * Exception: Super Admins (admin + reviewer) are allowed everywhere. */
function PartnerStrictRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading...</div>;
  const isSuperAdmin = user?.is_admin && user?.is_fortinet_reviewer;
  if (isSuperAdmin) return children;
  if (user?.is_admin) return <Navigate to="/admin" replace />;
  if (user?.is_fortinet_reviewer) return <Navigate to="/reviews" replace />;
  return children;
}

/** /contact is a public page (visitors browsing the marketing site can reach
 * it without login). When a logged-in admin or reviewer hits it, redirect to
 * their respective home so they don't see partner-facing content.
 * Exception: Super Admins are allowed through. */
function PartnerStrictGuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading...</div>;
  const isSuperAdmin = user?.is_admin && user?.is_fortinet_reviewer;
  if (isSuperAdmin) return children;
  if (user?.is_admin) return <Navigate to="/admin" replace />;
  if (user?.is_fortinet_reviewer) return <Navigate to="/reviews" replace />;
  return children;
}

function Shell() {
  const [chatOpen, setChatOpen] = useState(false);
  const { user } = useAuth();
  const loc = useLocation();

  useEffect(() => { setChatOpen(false); }, [loc.pathname]);

  // Force the browser tab title to reflect the new brand even with HMR.
  useEffect(() => { document.title = "Pythagore — BOM · BOQ Engine"; }, []);

  const openChat = () => {
    if (!user) { toast.info("Connectez-vous pour utiliser l'assistant IA"); return; }
    setChatOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/configurator" element={<PrivateRoute><PartnerStrictRoute><Configurator /></PartnerStrictRoute></PrivateRoute>} />
          <Route path="/catalog" element={<PrivateRoute><PartnerStrictRoute><Catalog /></PartnerStrictRoute></PrivateRoute>} />
          <Route path="/quotes" element={<PrivateRoute><PartnerStrictRoute><Quotes /></PartnerStrictRoute></PrivateRoute>} />
          <Route path="/quotes/validated" element={<PrivateRoute><PartnerStrictRoute><ValidatedQuotes /></PartnerStrictRoute></PrivateRoute>} />
          <Route path="/pricelist" element={<PrivateRoute><PartnerStrictRoute><PriceList /></PartnerStrictRoute></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/reviews" element={<PrivateRoute><Reviews /></PrivateRoute>} />
          <Route path="/contact" element={<PartnerStrictGuestRoute><Contact /></PartnerStrictGuestRoute>} />
          <Route path="/manual" element={<Manual />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Routes>
      </main>
      <footer className="border-t border-zinc-200 bg-white py-4">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between text-xs text-zinc-500">
          <span className="font-mono">Pythagore · BOM / BOQ engine v1</span>
          <span>Prix indicatifs · USD</span>
        </div>
      </footer>
      <ChatSheet open={chatOpen} onOpenChange={setChatOpen} />
      {user && (!user.is_admin || user.is_fortinet_reviewer) && <FloatingAssistant onOpenChat={openChat} />}
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
